import { cx } from '@emotion/css';
import type { Gallery as GalleryType } from '@snapchat/mw-contentful-schema';
import {
  editorialGalleryCardsContainerCss,
  GalleryFiltersV2 as GalleryFiltersV2SDS,
  Pagination,
} from '@snapchat/snap-design-system-marketing';
import cloneDeep from 'lodash-es/cloneDeep';
import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';
import type { FC } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { AppContext } from '../../AppContext';
import { Config } from '../../config';
import { getContentfulInspectorProps } from '../../utils/contentful/getContentfulInspectorProps';
import { ConsumerContext } from '../ConsumerContextProvider';
import { defaultEditorialTilesPerPage, galleryTagsPrefix } from './constants';
import { EditorialGalleryLazyTiles } from './EditorialGalleryLazyTiles';
import { type GalleryProps, handleScroll } from './Gallery';
import { editorialGalleryPaginationCss, editorialGalleryWrapperCss } from './styles';
import { useGalleryTiles } from './useGalleryTiles';
import { buildSearchMenus } from './utils';

export const EditorialGallery: FC<GalleryProps> = props => {
  const { gallery } = props;
  const galleryId = gallery.sys.id;

  const { getUrlParams, setUrlParams } = useContext(ConsumerContext);
  const { onRedirect, getCurrentUrl } = useContext(AppContext);

  const query = getUrlParams?.() ?? {};
  const [page, setPage] = useState(gallery.hidePagination ? 1 : Number(query?.page ?? 1));

  const dropdownFilters = useMemo(() => {
    if (gallery?.dropdownsCollection) {
      return buildSearchMenus(gallery.dropdownsCollection.items, true);
    }

    return undefined;
  }, [gallery]);

  const dropdownFiltersIds = useMemo(() => {
    if (!dropdownFilters) {
      return [];
    }

    return dropdownFilters.map(dropdown => dropdown.id);
  }, [dropdownFilters]);

  const [tilesTags, setTilesTags] = useState(pick(query, dropdownFiltersIds));

  // Handle back button click
  useEffect(() => {
    const searchParams = getUrlParams?.() ?? {};
    const newFilters = pick(searchParams, dropdownFiltersIds);
    const newPage = Number(searchParams?.page ?? 1);
    setTilesTags(newFilters);
    setPage(newPage);
  }, [dropdownFiltersIds, getUrlParams]);

  const { paginationData, isLoading } = useGalleryTiles({
    galleryId,
    page,
    pageSize: gallery?.itemsPerPage || defaultEditorialTilesPerPage,
    tags: Object.values(tilesTags),
    sortBy: gallery?.sortBy,
  });

  const galleryRef = useRef<HTMLDivElement>(null);

  const onChangeFilter = useCallback(
    (filterKey: string, filterValue: string | undefined) => {
      if (!setUrlParams) {
        return;
      }

      let newFilters = cloneDeep(tilesTags);
      const currentQuery = getUrlParams?.() ?? {};
      const otherParams = omit(currentQuery, dropdownFiltersIds);

      if (filterValue) {
        newFilters[filterKey] = filterValue;
      } else {
        newFilters = omit(newFilters, filterKey);
      }
      setTilesTags(newFilters);
      setPage(1);
      setUrlParams({ ...otherParams, ...newFilters, page: '1' });
    },
    [dropdownFiltersIds, getUrlParams, setUrlParams, tilesTags]
  );

  const onChangePage = useCallback(
    (newPage: number) => {
      if (!setUrlParams) {
        return;
      }

      const currentQuery = getUrlParams?.() ?? {};
      const newUrlParams = cloneDeep(currentQuery);
      newUrlParams.page = `${newPage}`;
      setPage(newPage);
      setUrlParams(newUrlParams);
      handleScroll(galleryRef.current);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getUrlParams, setUrlParams, galleryRef.current]
    /*
     * TODO: Take a look to this again.
     * This won't work as intended because the component won't re-render when the ref is assigned.
     * The proper fix could be to pass the ref itself into the handleScroll as oppossed to the .current
     */
  );

  const clearFilters = useCallback(() => {
    if (!setUrlParams) {
      return;
    }

    const currentQuery = getUrlParams?.() ?? {};
    const otherParams = omit(currentQuery, dropdownFiltersIds);
    setPage(1);
    setTilesTags({});
    setUrlParams({ ...otherParams, page: '1' });
  }, [dropdownFiltersIds, getUrlParams, setUrlParams]);

  const { mobileFiltersWrapperTextDataset, clearFiltersCtaCopyDataset, emptyPlaceholderDataset } =
    getContentfulInspectorProps<GalleryType>({
      entryId: galleryId,
      fieldIds: [
        'emptyPlaceholder',
        'loadMoreCtaCopy',
        'mobileFiltersWrapperText',
        'clearFiltersCtaCopy',
      ],
    });

  if (onRedirect && Config?.redirects?.queryRedirects) {
    const queryRedirectsKeys = new Set(Object.keys(Config.redirects.queryRedirects));
    const queryKeys = Object.keys(query);
    const hasMatch = queryKeys.some(key => queryRedirectsKeys.has(key));

    if (hasMatch) {
      const requestUrl = new URL(getCurrentUrl());
      const url = new URL(`${requestUrl.protocol}//${requestUrl.host}${requestUrl.pathname}`);

      for (const [key, value] of Object.entries(query)) {
        if (queryRedirectsKeys.has(key)) {
          url.searchParams.set(`${galleryTagsPrefix}${key}`, String(value));
          continue;
        }

        url.searchParams.set(key, String(value));
      }

      // Copy the hash/fragment (thing after #)
      url.hash = requestUrl.hash;

      onRedirect(`${url.pathname}${url.search}${url.hash}`, { newTab: false });
      return null;
    }
  }

  return (
    <div
      data-testid="mwp-lazy-editorial-gallery"
      ref={galleryRef}
      className={editorialGalleryWrapperCss}
    >
      {dropdownFilters && !gallery.hideFilters ? (
        <GalleryFiltersV2SDS
          searchMenus={dropdownFilters}
          selectedFilters={tilesTags}
          onChangeFilter={(filterKey, filterValue) => onChangeFilter(filterKey, filterValue)}
          onClearFilters={clearFilters}
          clearButtonLabel={gallery?.clearFiltersCtaCopy}
          mobileFiltersToggleLabel={gallery?.mobileFiltersWrapperText}
          mobileFiltersWrapperTextDataset={mobileFiltersWrapperTextDataset}
          clearFiltersCtaCopyDataset={clearFiltersCtaCopyDataset}
        />
      ) : null}
      <div
        className={cx({
          [editorialGalleryCardsContainerCss]:
            !isLoading && paginationData && paginationData?.tiles?.length > 0,
        })}
      >
        <EditorialGalleryLazyTiles
          emptyPlaceholder={gallery.emptyPlaceholder}
          tiles={paginationData?.tiles}
          emptyPlaceholderDataset={emptyPlaceholderDataset}
          isGalleryLoading={isLoading}
          defaultTileImage={gallery.defaultImage ?? undefined}
          numberOfTiles={gallery?.itemsPerPage ?? defaultEditorialTilesPerPage}
        />
      </div>
      {paginationData?.total && !gallery.hidePagination ? (
        <Pagination
          totalPages={
            Math.floor(
              (paginationData.total - 1) / (gallery?.itemsPerPage ?? defaultEditorialTilesPerPage)
            ) + 1
          }
          currentPage={page}
          onChange={onChangePage}
          className={editorialGalleryPaginationCss}
        />
      ) : null}
    </div>
  );
};

EditorialGallery.displayName = 'EditorialGallery';
